import {
  ChangeEvent,
  ComponentType,
  ReactNode,
  SVGProps,
  useEffect,
  useRef,
  useState,
} from "react";
import { Copy, Download, FileText, Info, Link2, Loader2, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { SubcategoryDetailProps } from "../constants/settingsStructure";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/modern/components/ui/alert-dialog";
import { Button } from "@/modern/components/ui/button";
import { Card } from "@/modern/components/ui/card";
import { Label } from "@/modern/components/ui/label";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import {
  getMcpWorkflowDocument,
  getMcpWorkflowDocumentDownloadUrl,
  setMcpWorkflowDocument,
  uploadFile,
} from "@/network/network";
import { copyTextToClipboard, downloadFileFromData } from "@/other/utils/utils";
import { IntegrationAlert } from "@/integrations";
import { useBoardPermissions } from "../hooks/useBoardPermissions";
import {
  buildSwivuMcpConnectPrompt,
  buildSwivuMcpWorkflowPrompt,
  getSwivuMcpServerUrl,
} from "./swivuMcpPrompt";

type LucideIcon = ComponentType<SVGProps<SVGSVGElement> & { className?: string }>;

type SwivuMcpWorkflowMeta = {
  fileName: string;
};

const MCP_URL = getSwivuMcpServerUrl();
const CONNECT_PROMPT = buildSwivuMcpConnectPrompt();
const WORKFLOW_PROMPT = buildSwivuMcpWorkflowPrompt();
const OWNER_ONLY_UPDATE_MESSAGE = "Only the board owner can update the workflow file";

const isMarkdownFileName = (name: string): boolean => /\.md$/i.test(name);

const StepHeader = ({
  step,
  title,
  description,
}: {
  step: number;
  title: string;
  description: string;
}) => (
  <div className="flex items-start gap-3">
    <div className="flex-shrink-0 w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
      <span className="text-sm font-bold">{step}</span>
    </div>
    <div className="min-w-0 flex-1 space-y-1">
      <h3 className="text-base font-semibold text-foreground">{title}</h3>
      <p className="text-xs text-muted-foreground">{description}</p>
    </div>
  </div>
);

const ResourceRow = ({
  icon: Icon,
  title,
  subtitle,
  titleClassName = "text-base font-semibold text-foreground truncate",
  action,
}: {
  icon: LucideIcon;
  title: string;
  subtitle: string;
  titleClassName?: string;
  action: ReactNode;
}) => (
  <div className="flex items-center gap-3 rounded-lg border border-border/60 bg-muted/20 px-3 py-3">
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
      <Icon className="h-5 w-5" />
    </div>
    <div className="min-w-0 flex-1">
      <p className={titleClassName}>{title}</p>
      <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>
    </div>
    {action}
  </div>
);

const PromptPreview = ({
  label,
  prompt,
  copyLabel,
  toastMessage,
}: {
  label?: string;
  prompt: string;
  copyLabel: string;
  toastMessage: string;
}) => (
  <div className="space-y-2">
    {label && <Label className="text-xs text-muted-foreground">{label}</Label>}
    <div className="rounded-md border border-border bg-background">
      <pre className="max-h-72 overflow-auto p-3 text-xs whitespace-pre-wrap font-mono text-foreground">
        {prompt}
      </pre>
    </div>
    <Button
      type="button"
      onClick={() => copyTextToClipboard(prompt, toastMessage)}
      className="w-full sm:w-auto"
    >
      <Copy className="h-4 w-4 mr-2" />
      {copyLabel}
    </Button>
  </div>
);

/** Requires a linked document id so empty/cleared payloads stay null. */
const parseWorkflowMeta = (data: unknown): SwivuMcpWorkflowMeta | null => {
  if (!data || typeof data !== "object") return null;
  const documentId = (data as { document_id?: unknown }).document_id;
  const fileName = (data as { filename?: unknown }).filename;
  if (typeof documentId !== "number" || typeof fileName !== "string" || !fileName) {
    return null;
  }
  return { fileName };
};

const SwivuMcpDetail = ({ subcategory }: SubcategoryDetailProps) => {
  const { selectedBoard } = useSwivuBryntumContext();
  const { canWrite: canEditWorkflow } = useBoardPermissions();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [workflow, setWorkflow] = useState<SwivuMcpWorkflowMeta | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);

  const boardId = selectedBoard?.id;

  useEffect(() => {
    let cancelled = false;

    const loadWorkflow = async () => {
      if (!boardId) {
        setWorkflow(null);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const { data } = await getMcpWorkflowDocument(boardId);
        if (cancelled) return;
        setWorkflow(parseWorkflowMeta(data));
      } catch (error) {
        if (cancelled) return;
        console.error("Failed to load Swivu MCP workflow metadata", error);
        setWorkflow(null);
        toast.error("Failed to load workflow file");
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void loadWorkflow();
    return () => {
      cancelled = true;
    };
  }, [boardId]);

  const handleDownload = async () => {
    if (!boardId || !workflow) return;
    setIsDownloading(true);
    try {
      const { data } = await getMcpWorkflowDocumentDownloadUrl(boardId);
      const downloadUrl = data?.download_url;
      if (!downloadUrl || typeof downloadUrl !== "string") {
        throw new Error("Download URL missing");
      }
      const response = await fetch(downloadUrl);
      if (!response.ok) {
        throw new Error(`Download failed (${response.status})`);
      }
      const blob = await response.blob();
      downloadFileFromData(blob, workflow.fileName);
    } catch (error) {
      console.error("Failed to download Swivu MCP workflow file", error);
      toast.error("Failed to download workflow file");
    } finally {
      setIsDownloading(false);
    }
  };

  const handleClear = async () => {
    if (!boardId || !canEditWorkflow) {
      toast.error(OWNER_ONLY_UPDATE_MESSAGE);
      return;
    }

    setIsClearing(true);
    try {
      await setMcpWorkflowDocument(boardId, null);
      setConfirmRemoveOpen(false);
      setWorkflow(null);
      toast.success("Workflow file removed");
    } catch (error) {
      console.error("Failed to clear Swivu MCP workflow file", error);
      toast.error("Failed to remove workflow file");
    } finally {
      setIsClearing(false);
    }
  };

  const handleFileSelected = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    if (!isMarkdownFileName(file.name)) {
      toast.error("Only .md markdown files are allowed");
      return;
    }

    if (!boardId || !canEditWorkflow) {
      toast.error(OWNER_ONLY_UPDATE_MESSAGE);
      return;
    }

    setIsUploading(true);
    try {
      const uploadResponse = await uploadFile(file, file.name);
      const uploadedId = Number(uploadResponse?.data?.id);
      if (!uploadedId) {
        throw new Error("Upload did not return a document id");
      }

      const { data } = await setMcpWorkflowDocument(boardId, uploadedId);
      setWorkflow(parseWorkflowMeta(data) ?? { fileName: file.name });
      toast.success(workflow ? "Workflow file replaced" : "Workflow file uploaded");
    } catch (error) {
      console.error("Failed to upload Swivu MCP workflow file", error);
      toast.error("Failed to upload workflow file");
    } finally {
      setIsUploading(false);
    }
  };

  let workflowBody: ReactNode;
  if (isLoading) {
    workflowBody = <p className="text-sm text-muted-foreground">Loading…</p>;
  } else if (workflow) {
    workflowBody = (
      <div className="space-y-4">
        <ResourceRow
          icon={FileText}
          title={workflow.fileName}
          subtitle="Markdown workflow file"
          action={
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="shrink-0 bg-background"
              disabled={isDownloading}
              aria-label={isDownloading ? "Downloading…" : "Download workflow file"}
              onClick={() => void handleDownload()}
            >
              {isDownloading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
            </Button>
          }
        />

        {canEditWorkflow && (
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              disabled={isUploading || isClearing}
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="h-4 w-4 mr-2" />
              {isUploading ? "Uploading…" : "Replace .md"}
            </Button>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              disabled={isUploading || isClearing}
              onClick={() => setConfirmRemoveOpen(true)}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Remove
            </Button>
          </div>
        )}

        <div className="space-y-3 border-t border-border pt-4">
          <div>
            <h4 className="text-sm font-semibold text-foreground">Workflow prompt</h4>
            <p className="text-xs text-muted-foreground mt-0.5">
              Download the .md file, attach it in your AI client, then paste this prompt.
            </p>
          </div>
          <PromptPreview
            prompt={WORKFLOW_PROMPT}
            copyLabel="Copy workflow prompt"
            toastMessage="Workflow prompt copied"
          />
        </div>
      </div>
    );
  } else if (canEditWorkflow) {
    workflowBody = (
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">
          No workflow file yet. Upload a .md file to unlock the workflow prompt.
        </p>
        <Button
          type="button"
          disabled={isUploading}
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload className="h-4 w-4 mr-2" />
          {isUploading ? "Uploading…" : "Upload .md"}
        </Button>
      </div>
    );
  } else {
    workflowBody = (
      <div className="space-y-4">
        <IntegrationAlert
          variant="missing_config"
          title="Workflow file not configured"
          message="No board workflow file has been added yet. To use the workflow prompt, the Board Owner needs to upload a .md workflow file."
        />
        <Card className="py-3 px-4 border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-900/10">
          <div className="flex gap-3">
            <Info className="h-4 w-4 text-gray-600 dark:text-gray-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-gray-900 dark:text-gray-100">
              Only the <strong>Board Owner</strong> can upload or replace the board workflow file.
            </p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-foreground mb-2">{subcategory.label}</h2>
        <p className="text-sm text-muted-foreground">{subcategory.description}</p>
        <p className="text-xs text-muted-foreground mt-2">
          Works with AI clients that support remote MCP servers, such as ChatGPT, Claude, or
          Gemini. Connect Swivu MCP first, then share the board workflow.
        </p>
      </div>

      <div className="space-y-5 max-w-3xl">
        <Card className="py-4 px-4 space-y-4">
          <StepHeader
            step={1}
            title="Connect Swivu MCP"
            description="Paste the prompt into your AI client. It will walk you through adding the MCP server URL in its settings and signing in, then verify the connection."
          />

          <ResourceRow
            icon={Link2}
            title={MCP_URL}
            subtitle="MCP server URL"
            titleClassName="text-base font-semibold font-mono text-foreground break-all"
            action={
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="shrink-0 bg-background"
                aria-label="Copy MCP server URL"
                onClick={() => copyTextToClipboard(MCP_URL, "MCP URL copied")}
              >
                <Copy className="h-4 w-4" />
              </Button>
            }
          />

          <PromptPreview
            label="Connect prompt"
            prompt={CONNECT_PROMPT}
            copyLabel="Copy Prompt"
            toastMessage="MCP connect prompt copied"
          />
        </Card>

        <Card className="py-4 px-4 space-y-4">
          <StepHeader
            step={2}
            title="Board workflow"
            description="Once Swivu MCP is connected, paste the workflow prompt into the same chat so your AI client follows how this board works."
          />

          {workflowBody}

          {canEditWorkflow && (
            <input
              ref={fileInputRef}
              type="file"
              accept=".md,text/markdown"
              className="hidden"
              onChange={handleFileSelected}
            />
          )}
        </Card>
      </div>

      <AlertDialog
        open={confirmRemoveOpen}
        onOpenChange={(open) => !isClearing && setConfirmRemoveOpen(open)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove workflow file?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the board workflow file
              {workflow?.fileName ? ` (${workflow.fileName})` : ""}. Anyone using the
              workflow prompt will need a new file uploaded.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isClearing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={isClearing}
              onClick={(event) => {
                event.preventDefault();
                void handleClear();
              }}
            >
              {isClearing ? "Removing…" : "Remove"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SwivuMcpDetail;
